import React, { useEffect, useId, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  LOCALE_LABELS,
  SUPPORTED_LOCALES,
  persistLocale,
  swapLocaleInPath,
} from "./config";
import { useLocale } from "./useLocale";

export const LanguageSwitcher = ({ className = "" }) => {
  const locale = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const listId = useId();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const onPointerDown = (event) => {
      if (rootRef.current && !rootRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
      }
    };

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname, locale]);

  if (SUPPORTED_LOCALES.length === 0) {
    return null;
  }

  const onChange = (nextLocale) => {
    if (nextLocale === locale) {
      setOpen(false);
      return;
    }
    persistLocale(nextLocale);
    const nextPath = swapLocaleInPath(location.pathname, nextLocale);
    navigate(`${nextPath}${location.search}${location.hash}`, {
      state: location.state,
    });
    setOpen(false);
  };

  if (SUPPORTED_LOCALES.length === 1) {
    return (
      <span className={`language-switcher language-switcher--single ${className}`}>
        {LOCALE_LABELS[locale] || locale.toUpperCase()}
      </span>
    );
  }

  return (
    <div
      className={`language-switcher ${open ? "is-open" : ""} ${className}`}
      ref={rootRef}
    >
      <button
        type="button"
        className="language-switcher__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={t("language")}
        onClick={() => setOpen((value) => !value)}
      >
        <i className="fas fa-globe language-switcher__icon" aria-hidden="true" />
        <span className="language-switcher__label">
          {LOCALE_LABELS[locale] || locale.toUpperCase()}
        </span>
        <i
          className={`fas fa-chevron-down language-switcher__caret ${
            open ? "is-open" : ""
          }`}
          aria-hidden="true"
        />
      </button>

      <div
        id={listId}
        className="language-switcher__menu"
        role="listbox"
        aria-label={t("language")}
        hidden={!open}
      >
        {SUPPORTED_LOCALES.map((code) => {
          const active = code === locale;
          return (
            <button
              key={code}
              type="button"
              role="option"
              aria-selected={active}
              className={`language-switcher__option ${
                active ? "is-active" : ""
              }`}
              onClick={() => onChange(code)}
            >
              <span className="language-switcher__option-label">
                {LOCALE_LABELS[code] || code.toUpperCase()}
              </span>
              {active && (
                <i className="fas fa-check language-switcher__check" aria-hidden="true" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};
